import React, { useState } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  vagaId: string;
  modoIndicacao: boolean; // true = user comum indicando; false = RH cadastrando
  onClose: () => void;
  onSalvo: () => void;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400';
const labelCls = 'text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block';

function mascararCPF(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

const ModalCandidato: React.FC<Props> = ({ user, vagaId, modoIndicacao, onClose, onSalvo }) => {
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', cpf: '', origem: modoIndicacao ? 'indicacao' : 'site', curriculo_url: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }));

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    // RH pode cadastrar só com o nome (o e-mail é obrigatório na indicação)
    if ((modoIndicacao || form.email.trim()) && !EMAIL_RE.test(form.email)) { setErro('E-mail inválido.'); return; }
    const cpfDigits = form.cpf.replace(/\D/g, '');
    if (cpfDigits && cpfDigits.length !== 11) { setErro('CPF deve ter 11 dígitos.'); return; }
    setSalvando(true); setErro(null);
    try {
      await atlasApi.criarCandidato(user, vagaId, {
        nome: form.nome, email: form.email.trim() || undefined, telefone: form.telefone || undefined,
        cpf: cpfDigits || undefined, origem: form.origem, curriculo_url: form.curriculo_url || undefined,
      });
      onSalvo();
    } catch (e: any) { setErro(e.message || 'Erro ao salvar.'); setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex-1">{modoIndicacao ? 'Indicar candidato' : 'Novo candidato'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className={labelCls}>Nome *</label><input className={inputCls} value={form.nome} onChange={e => set('nome', e.target.value)} /></div>
          <div><label className={labelCls}>E-mail{modoIndicacao ? ' *' : ''}</label><input className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Telefone</label><input className={inputCls} value={form.telefone} onChange={e => set('telefone', e.target.value)} /></div>
            <div><label className={labelCls}>CPF</label><input className={inputCls} value={form.cpf} placeholder="000.000.000-00" onChange={e => set('cpf', mascararCPF(e.target.value))} /></div>
          </div>
          {!modoIndicacao && (
            <div>
              <label className={labelCls}>Origem</label>
              <select className={inputCls} value={form.origem} onChange={e => set('origem', e.target.value)}>
                <option value="site">Site</option><option value="linkedin">LinkedIn</option><option value="indicacao">Indicação</option><option value="presencial">Presencial</option>
              </select>
            </div>
          )}
          <div><label className={labelCls}>Currículo (URL)</label><input className={inputCls} value={form.curriculo_url} onChange={e => set('curriculo_url', e.target.value)} placeholder="https://..." /></div>

          {erro && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm"><AlertCircle className="w-4 h-4" /> {erro}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalCandidato;
